import { setIconColor } from 'modules/utils';
import styled from 'styled-components';
import { theme } from '../theme';
import { ButtonType } from './ButtonType';

export interface StyledButtonProps {
  buttonType: ButtonType;
  disabled?: boolean;
  color?: string;
  hoverColor?: string;
}

const regularButtonFontSize = theme.fontSizes.body;
const textAndActionButtonFontSize = theme.fontSizes.h5;

export const StyledButtonIconContainer = styled.div<StyledButtonProps>`
  ${(props) => (props.buttonType === ButtonType.Text ? 'width: 0.8rem;' : '')}

  height: ${(props) => {
    if (
      props.buttonType === ButtonType.Primary ||
      props.buttonType === ButtonType.Secondary
    ) {
      return regularButtonFontSize;
    }
    if (
      props.buttonType === ButtonType.Action ||
      props.buttonType === ButtonType.Icon
    ) {
      return textAndActionButtonFontSize;
    }
    return '0.8rem';
  }};

  display: flex;
  align-items: center;
  justify-content: center;

  * {
    width: auto;
    height: 100%;
  }
`;

export const StyledButtonTextContainer = styled.div<StyledButtonProps>`
  padding: 0 0.4rem 0 0;
  white-space: nowrap;
  font-size: var(--h5-font-size);
  line-height: var(--h5-line-height);
  word-spacing: var(--word-spacing);

  ${(props) =>
    props.buttonType === ButtonType.Icon
      ? `
    font-weight: ${theme.fontWeight.bold};
    text-transform: uppercase;
    user-select: none;
    `
      : ''};

  ${(props) =>
    props.buttonType === ButtonType.SecondaryAction ||
    props.buttonType === ButtonType.Filled
      ? `
        padding-left: 0.4rem;
        `
      : ''};
`;

export const StyledButton = styled.button<StyledButtonProps>`
  outline: 0;
  border: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: ${theme.fontWeight.bold};
  cursor: pointer;
  border-radius: 4px;
  text-decoration: var(--text-underline);

  ${(props) => {
    if (
      props.buttonType === ButtonType.Primary ||
      props.buttonType === ButtonType.Secondary
    ) {
      return `
        height: 3.125rem;
        min-height: 3.125rem;
      `;
    }
    return ``;
  }};

  /* Only buttons with background or border have padding. */
  padding: ${(props) =>
    props.buttonType === ButtonType.Primary ||
    props.buttonType === ButtonType.Secondary
      ? '0 3rem'
      : props.buttonType === ButtonType.Filled
      ? '0 1.2rem'
      : '0'};

  /* Prevents svg icon from moving the element content up. */
  vertical-align: middle;

  /* Adding button border does not increase button size. */
  box-sizing: border-box;

  font-size: ${(props) =>
    props.buttonType === ButtonType.Primary ||
    props.buttonType === ButtonType.Secondary
      ? regularButtonFontSize
      : textAndActionButtonFontSize};

  border: ${(props) =>
    props.buttonType === ButtonType.Primary ||
    props.buttonType === ButtonType.Secondary
      ? `2px solid ${
          props.buttonType === ButtonType.Secondary
            ? props.theme.colors.primaryK10
            : props.theme.colors.primaryT2
        }`
      : `0`};

  flex-direction: ${(props) =>
    props.buttonType === ButtonType.Text ? `row-reverse` : `row`};

  text-transform: ${(props) =>
    props.buttonType === ButtonType.Primary ||
    props.buttonType === ButtonType.Secondary
      ? 'none'
      : 'uppercase'};

  ${(props) => {
    if (props.disabled) {
      return `
        background-color: ${
          props.buttonType === ButtonType.Primary ||
          props.buttonType === ButtonType.Secondary
            ? props.theme.colors.primaryK8
            : 'transparent'
        };

        color: ${props.theme.colors.primaryK3};
        border-color: ${props.theme.colors.primaryK1};

        ${StyledButtonIconContainer} {
          ${setIconColor(props.theme.colors.primaryK3)}
        };
      `;
    }
    if (props.buttonType === ButtonType.Primary) {
      return `
        background-color: ${props.theme.colors.primaryT2};
        color: ${props.theme.colors.primaryW};
        
        &:hover {
          border-color: ${props.theme.colors.primaryT1}; 
          background-color: ${props.theme.colors.primaryT1};
        }
    
        ${StyledButtonIconContainer} {
          ${setIconColor(props.theme.colors.primaryW)}
        }`;
    }
    if (props.buttonType === ButtonType.Filled) {
      return `
        background-color: ${props.color || props.theme.colors.primaryK1};
        color: ${props.theme.colors.primaryK10};
        ${StyledButtonIconContainer} {
          ${setIconColor(props.theme.colors.primaryK10)}
        }`;
    }
    if (props.buttonType === ButtonType.Secondary) {
      return `
        background-color: transparent;
        color: ${props.color || props.theme.colors.primaryK10};
        border-color: ${props.color || props.theme.colors.primaryK10};
        
        &:hover {
          border-color: ${props.hoverColor || props.theme.colors.primaryK7};
          color: ${props.hoverColor || props.theme.colors.primaryK7};

          & ${StyledButtonIconContainer} {
            ${setIconColor(props.theme.colors.primaryK7)}
          }
        }`;
    }
    if (props.buttonType === ButtonType.Text) {
      return `
        background-color: transparent;
        color: ${props.color || props.theme.colors.primaryT2};

        ${StyledButtonIconContainer} {
          ${setIconColor(props.color || props.theme.colors.primaryT2)}
        }

        &:hover {
          color: ${props.hoverColor || props.theme.colors.primaryT1};
          & ${StyledButtonIconContainer} {
            ${setIconColor(props.hoverColor || props.theme.colors.primaryT1)}
          }
        }`;
    }
    if (props.buttonType === ButtonType.Action) {
      return `
        background-color: transparent;
        color: ${props.color || props.theme.colors.primaryT2};
    
        ${StyledButtonIconContainer} {
          ${setIconColor(props.color || props.theme.colors.primaryT2)}
        }
        
        &:hover {
          color: ${props.hoverColor || props.theme.colors.primaryT1};
          & ${StyledButtonIconContainer} {
            ${setIconColor(props.hoverColor || props.theme.colors.primaryT1)}
          }
        }`;
    }
    if (props.buttonType === ButtonType.SecondaryAction) {
      return `
        background-color: transparent;
        color: ${props.theme.colors.primaryK10};
    
        ${StyledButtonIconContainer} {
          ${setIconColor(props.theme.colors.primaryK10)}
        }
        
        &:hover {
          color: ${props.theme.colors.primaryK7};
          & ${StyledButtonIconContainer} {
            ${setIconColor(props.theme.colors.primaryK7)}
          }
        }`;
    }
    if (props.buttonType === ButtonType.Icon) {
      return `
        background-color: transparent;

        ${StyledButtonIconContainer} {
          ${setIconColor(props.color || props.theme.colors.primaryT2)}
        }
        ${StyledButtonTextContainer} {
          color: ${props.color || props.theme.colors.primaryT2};
        }
        
        &:hover {
          ${StyledButtonIconContainer} {
            ${setIconColor(props.hoverColor || props.theme.colors.primaryT1)}
          }
          ${StyledButtonTextContainer} {
            color: ${props.hoverColor || props.theme.colors.primaryT1};
          }
        }`;
    }
    return null;
  }}
`;
